<template>
  <div class="root-cause-table" v-if="hasData">
    <h3>Root Cause(s)</h3>
    <b-table
      id="events-table"
      responsive
      small
      primary-key="id"
      :items="events"
      :fields="tableFields"
      :per-page="perPage"
      :current-page="currentPage"
    >
      <template #table-colgroup="scope">
        <col
          v-for="field in scope.fields"
          :key="field.key"
          :style="{ width: colWidth(field.key), minWidth: colMinWidth(field.key) }"
        >
      </template>
      <template #cell(time)="row">
        <b-row>
          <b-col>
            {{ formatDate(row.value) }}
          </b-col>
        </b-row>
      </template>
      <template #cell(entities)="row">
        <div>
          <span :id="`popover-${row.index}`" v-html="entitiesSummary(row.value)"></span>
          <b-popover
            :target="`popover-${row.index}`"
            placement="bottomright"
            triggers="hover focus"
            custom-class="custom-popover"
          >
            <table class="popover-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Name</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="entity in row.value" :key="entity.id" class="popover-entity-item">
                  <td>{{ entity.type }}</td>
                  <td>{{ entity.name }}</td>
                </tr>
              </tbody>
            </table>
          </b-popover>
        </div>
      </template>
    </b-table>
  </div>
</template>

<script>
import moment from 'moment';

import colorPalette from '@/xvisor/constants/colorPalette';
import momentTimeFormat from '@/xvisor/constants/momentTimeFormat';

export default {
  props: {
    alertId: {
      type: String,
      required: true,
    },
    timeRange: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      events: [],
      perPage: 20,
      currentPage: 1,
      loaded: false,
      tableFields: [
        {
          key: 'type', label: 'Event Type', sortable: true, thStyle: { width: '15%' },
        },
        { key: 'time', label: 'Time', thStyle: { width: '10%' } },
        { key: 'eventMessage', label: 'Event Message', thStyle: { width: '25%' } },
        { key: 'entities', label: 'Location(s)', thStyle: { width: '50%' } },
      ],
      colorPalette,
    };
  },
  computed: {
    eventTableUrl() {
      return `/alerts/${this.alertId}/rootcause/events/list.json`;
    },
    paginationTotalRows() {
      return this.events.length;
    },
    hasData() {
      return this.loaded && this.events && this.events.length > 0;
    },
  },
  watch: {
    timeRange() {
      this.httpGet();
    },
  },
  mounted() {
    this.httpGet();
  },
  methods: {
    httpGet() {
      this.$http
        .get(this.eventTableUrl, {
          params: {
            start: this.timeRange.start.toISOString(),
            end: this.timeRange.end.toISOString(),
          },
        })
        .catch(() => {
          this.events = [];
        })
        .then((response) => { this.events = response.data; })
        .finally(() => {
          this.loaded = true;
        });
    },
    entitiesSummary(entities) {
      const maxDisplay = 3;
      const displayedEntities = entities.slice(0, maxDisplay);
      const moreEntitiesCount = entities.length - maxDisplay;

      const summary = displayedEntities
        .map((entity) => `${entity.type}: ${entity.name}`)
        .join(', ');

      return `<span class="text-primary">${summary}</span>
       ${moreEntitiesCount > 0 ? `<span class="text-muted">+ ${moreEntitiesCount} more</span>` : ''}`;
    },
    formatDate(dateStr) {
      return moment(dateStr).format(momentTimeFormat.dateTime);
    },
    colWidth(fieldKey) {
      switch (fieldKey) {
        case 'type': return '10%';
        case 'time': return '10%';
        case 'eventMessage': return '35%';
        case 'entities': return '10%';
        default: return '180px';
      }
    },
    colMinWidth(fieldKey) {
      switch (fieldKey) {
        case 'type': return '100px';
        case 'time': return '100px';
        case 'eventMessage': return '300px';
        case 'entities': return '100px';
        default: return '80px';
      }
    },
  },
};
</script>

<style scoped>
.root-cause-table {
  padding-top: 3%;
  padding-bottom: 3%;
}

.custom-popover {
  max-width: 800px; /* Increase max-width */
  white-space: normal;
}

.popover-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px; /* Increase font size */
}

.popover-table th, .popover-table td {
  padding: 10px; /* Increase padding */
}

.popover-table th {
  font-weight: bold;
  font-size: 14px; /* Larger font size for headers */
}

.popover-entity-item:last-child td {
  border-bottom: none;
}
</style>
